// src/oura/components/visibility/TypeGrid.tsx
//
// Named by question type: which engine misses which type (YW, 27 Sep). A
// cell is a count, "1 of 4": the type's questions the engine named, of the
// ones it answered. Never a percentage: four questions cannot carry "25%".
// The tint steps with the share, and a press opens exactly those questions
// in the table below, so the table lists as many rows as the "of".

import { LayoutGridIcon } from "lucide-react";
import { EngineLogo, FOCUS_RING, FoldCard, KindChip, TYPE } from "@oura/ui";
import { Tooltip } from "@component/tooltip";
import type { VisibilityCheckName } from "../../audit";
import { cn } from "../../lib/cn";
import { VISIBILITY_CHECK_LABEL } from "../../lib/visibilityChecks";
import { gridCell, namedRule, pct, VIS_ENGINES, type EngineFilter, type VisModel } from "../../visibility/model";

/** Four green steps, at 15, 30 and 45% named: enough to rank a row at a glance. */
const TINT = ["bg-gray-100", "bg-[#E3F7EE]", "bg-[#C8F0DD]", "bg-[#A6E7C8]"] as const;
const step = (p: number) => (p >= 45 ? 3 : p >= 30 ? 2 : p >= 15 ? 1 : 0);

const COLS: EngineFilter[] = ["all", ...VIS_ENGINES];

export function TypeGrid({
    model,
    kinds,
    engine,
    staff,
    onCell,
}: {
    model: VisModel;
    kinds: VisibilityCheckName[];
    engine: EngineFilter;
    staff: boolean;
    onCell: (kind: VisibilityCheckName, engine: EngineFilter) => void;
}) {
    const name = (e: EngineFilter) => (e === "all" ? "All" : model.engines[e].label);
    const countedRows = model.rows.filter((r) => r.kind !== null).length;
    const failed = model.engines.ai_overview.coverage?.failed ?? 0;
    return (
        <FoldCard
            alwaysOpen
            id="types"
            title="Named by question type"
            icon={{ icon: LayoutGridIcon, area: "ai" }}
            right={`${countedRows} questions`}
        >
            <p className={cn(TYPE.tm, "m-0 mb-3 text-gray-500")}>
                Questions of each type named, per AI. <span className="text-gray-400">{namedRule(model.counted, staff)}</span>
            </p>
            {/* A read that failed is not a miss (#4053): say how much of Google's
                column was never measured, from the engine's own count. Absent
                or 0 and the card is as it was. */}
            {failed > 0 && (
                <p className={cn(TYPE.tm, "m-0 mb-3 text-amber-700")}>
                    Google's AI: {failed} of {model.engines.ai_overview.coverage?.asked} not measured, Google could not be read.
                </p>
            )}
            <div role="table" aria-label="Named by question type and AI" className="grid grid-cols-[72px_repeat(4,minmax(0,1fr))] items-center gap-1 sm:grid-cols-[92px_repeat(4,minmax(0,1fr))] sm:gap-1.5">
                <div role="row" className="contents">
                    <span role="columnheader" aria-label="Question type" />
                    {COLS.map((e) => (
                        <span
                            key={e}
                            role="columnheader"
                            title={name(e)}
                            className={cn(
                                TYPE.tm,
                                "flex min-w-0 flex-wrap items-center justify-center gap-x-1.5 gap-y-0.5 text-center font-medium",
                                engine === e ? "text-blue-600" : "text-gray-500",
                            )}
                        >
                            {e !== "all" && <EngineLogo engine={e} size={14} />}
                            {/* The name drops under its logo when the column is
                                narrow, whole rather than cut. A phone gives each
                                column about 60px: the logo carries the engine
                                there, and the name stays for screen readers. */}
                            <span className={cn(e !== "all" && "max-sm:sr-only")}>{name(e)}</span>
                        </span>
                    ))}
                </div>
                {kinds.map((k) => {
                    const copy = model.kindCopy[k];
                    // The check's own question, and for staff the work that
                    // closes it: four types, four different jobs.
                    const tip = copy ? (staff ? `${copy.question} Fix: ${copy.fix}` : copy.question) : undefined;
                    return (
                        <div role="row" key={k} className="contents">
                            <span role="rowheader" className="min-w-0">
                                <KindChip kind={k} title={tip} />
                            </span>
                            {COLS.map((e) => {
                                const cell = gridCell(model.rows, k, e);
                                const dim = engine !== "all" && engine !== e;
                                const share = cell ? pct(cell.named, cell.of) : null;
                                if (cell && cell.of === 0 && cell.unread > 0) {
                                    // Every one of this type's reads failed: not measured, never "0 of n".
                                    return (
                                        <span
                                            key={e}
                                            role="cell"
                                            title={`${name(e)}: none of the ${cell.unread} ${VISIBILITY_CHECK_LABEL[k].toLowerCase()} questions could be read`}
                                            className={cn(
                                                "grid h-14 place-items-center rounded-xl bg-gray-50 px-1 text-center text-gray-400",
                                                TYPE.tm,
                                                dim && "opacity-40",
                                            )}
                                        >
                                            not measured
                                        </span>
                                    );
                                }
                                if (!cell || share === null) {
                                    return (
                                        <span
                                            key={e}
                                            role="cell"
                                            title={e === "ai_overview" && !model.engines.ai_overview.perQuestion
                                                ? "Read as a total on this scan, not per question"
                                                : "Not measured"}
                                            className={cn("grid h-14 place-items-center rounded-xl bg-gray-50 text-gray-300", dim && "opacity-40")}
                                        >
                                            –
                                        </span>
                                    );
                                }
                                return (
                                    <span role="cell" key={e} className="min-w-0">
                                        <Tooltip asChild content="View details">
                                        <button
                                            type="button"
                                            onClick={() => onCell(k, e)}
                                            aria-label={`${name(e)}: ${cell.named} of ${cell.of} ${VISIBILITY_CHECK_LABEL[k].toLowerCase()} questions. View details.`}
                                            // A blue ring and a lift on hover, so a cell reads as
                                            // something to press without a line saying so.
                                            className={cn(
                                                "flex h-14 w-full flex-col items-center justify-center rounded-xl transition-[box-shadow,opacity,transform] hover:-translate-y-px hover:shadow-[inset_0_0_0_2px_rgb(11,73,254),0_4px_10px_-4px_rgba(0,0,0,0.25)] motion-reduce:hover:translate-y-0",
                                                TINT[step(share)],
                                                dim && "opacity-40",
                                                FOCUS_RING,
                                            )}
                                        >
                                            {/* One line where it fits; the "of" drops under the
                                                count on a phone rather than being cut. */}
                                            <span className="flex flex-col items-center sm:flex-row sm:items-baseline sm:gap-1">
                                                <span className={cn(TYPE.tb, "font-semibold tabular-nums text-gray-900")}>{cell.named}</span>
                                                <span className={cn(TYPE.tm, "whitespace-nowrap tabular-nums text-gray-600")}>of {cell.of}{cell.unread > 0 ? " read" : ""}</span>
                                            </span>
                                        </button>
                                        </Tooltip>
                                    </span>
                                );
                            })}
                        </div>
                    );
                })}
            </div>
            <p className={cn(TYPE.tm, "m-0 mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-gray-500")}>
                Named less
                <span aria-hidden className="inline-flex gap-[3px]">
                    {TINT.map((t) => <i key={t} className={cn("block h-2.5 w-3.5 rounded-[3px]", t)} />)}
                </span>
                more
            </p>
        </FoldCard>
    );
}
